import {
  Badge,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdAutoAwesome, MdLock } from "react-icons/md";
import { useNavigate } from "react-router-dom";

/**
 * Generic upsell modal.
 *
 * Props:
 *   isOpen, onClose       — Chakra modal control
 *   title                 — e.g. "Workflows"
 *   description           — short paragraph describing the value
 *   availablePlans        — array of plan names: ["Ultra", "Agency Pro"]
 *   upgradePath           — optional, route to navigate to for upgrade
 *                            (defaults to /billing — verify route name fits this repo)
 *
 * Also exported as a content-only component (FeatureUpgradeContent) so the
 * same panel can be rendered inline on a page when a user direct-links to
 * a feature they don't have.
 */
export function FeatureUpgradeContent({
  title,
  description,
  availablePlans = [],
  upgradePath = "/settings/billing",
  onClose,
  variant = "modal",
}) {
  const navigate = useNavigate();
  const goUpgrade = () => navigate(upgradePath);
  return (
    <VStack
      align="stretch"
      spacing={4}
      p={variant === "inline" ? 8 : 0}
      maxW={variant === "inline" ? "560px" : "100%"}
      mx="auto"
    >
      <Flex
        align="center"
        justify="center"
        w="56px"
        h="56px"
        borderRadius="full"
        bg="purple.50"
        color="purple.500"
        alignSelf={variant === "inline" ? "center" : "flex-start"}
        mb={1}
      >
        <MdAutoAwesome size={28} />
      </Flex>

      <Text fontSize="xl" fontWeight="bold" textAlign={variant === "inline" ? "center" : "left"}>
        {title}
      </Text>
      <Text color="gray.600" fontSize="sm" textAlign={variant === "inline" ? "center" : "left"}>
        {description}
      </Text>

      {availablePlans.length > 0 && (
        <Flex direction="column" gap={2}>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase">
            Available on
          </Text>
          <HStack flexWrap="wrap" gap={2}>
            {availablePlans.map((p) => (
              <Badge key={p} colorScheme="purple" fontSize="sm" px={2} py={1} borderRadius="md">
                {p}
              </Badge>
            ))}
          </HStack>
        </Flex>
      )}

      <Flex gap={3} mt={3} justify={variant === "inline" ? "center" : "flex-end"}>
        {onClose && variant === "modal" && (
          <Button variant="ghost" onClick={onClose}>Close</Button>
        )}
        <Button colorScheme="purple" onClick={goUpgrade}>
          View plans
        </Button>
      </Flex>
    </VStack>
  );
}

export default function FeatureUpgradeModal({
  isOpen,
  onClose,
  title,
  description,
  availablePlans,
  upgradePath,
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={2} color="gray.500" fontSize="sm" fontWeight="normal">
            <MdLock size={14} /> Locked
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={2}>
          <FeatureUpgradeContent
            title={title}
            description={description}
            availablePlans={availablePlans}
            upgradePath={upgradePath}
            variant="modal"
          />
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
}
